/**
 * Screen Error Boundary Component
 * 
 * Wraps individual screens to catch and handle errors gracefully
 * without crashing the entire application. Provides user-friendly
 * error messages and recovery options.
 */

import React from 'react';
import * as Sentry from '@sentry/react';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';
import logger from '../utils/logger';

class ScreenErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console in development
    logger.error('Screen Error Boundary caught error:', error, errorInfo);
    
    // Update state with error details
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));
    
    // Report to Sentry with screen context
    Sentry.captureException(error, {
      tags: { screen: this.props.screenName || 'unknown' },
      contexts: { react: { componentStack: errorInfo.componentStack } }
    });

    // Track error in analytics if available
    if (window.analyticsService) {
      window.analyticsService.trackEvent('screen_error_boundary_triggered', {
        screen: this.props.screenName || 'unknown',
        error_message: error.toString(),
        component_stack: errorInfo.componentStack,
        error_count: this.state.errorCount + 1
      });
    }
  }

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
    
    // Call onReset prop if provided
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleGoHome = () => {
    // Navigate to dashboard
    if (this.props.onNavigateHome) {
      this.props.onNavigateHome();
    }
    
    // Reset error state
    this.handleReset();
  };

  render() {
    if (this.state.hasError) {
      const { screenName = 'This screen' } = this.props;
      
      return (
        <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" 
             style={{ background: 'var(--bg-app-gradient)' }}>
          
          {/* Animated gradient orbs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-purple-500/30 to-orange-500/30 rounded-full blur-3xl animate-float" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-orange-500/30 to-purple-500/30 rounded-full blur-3xl animate-float" 
                 style={{ animationDelay: '3s' }} />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-purple-500/20 to-orange-500/20 rounded-full blur-3xl animate-pulse" 
                 style={{ animationDuration: '4s' }} />
          </div>
          
          <div className="backdrop-blur-sm rounded-2xl shadow-2xl p-8 max-w-md w-full relative z-10"
               style={{
                 background: 'rgba(255, 255, 255, 0.05)',
                 border: '1px solid rgba(255, 255, 255, 0.1)'
               }}>
            <div className="flex items-center justify-center w-16 h-16 rounded-full mx-auto mb-4 backdrop-blur-sm"
                 style={{
                   background: 'rgba(239, 68, 68, 0.1)',
                   border: '1px solid rgba(239, 68, 68, 0.2)'
                 }}>
              <AlertCircle className="text-red-400" size={32} />
            </div>
            
            <h2 className="text-xl font-bold text-white text-center mb-2">
              Oops! Something went wrong
            </h2>
            
            <p className="text-white/70 text-center mb-6">
              {screenName} encountered an unexpected error. Don't worry, your data is safe.
            </p>
            
            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="rounded-lg p-3 mb-6 max-h-32 overflow-auto backdrop-blur-sm"
                   style={{
                     background: 'rgba(0, 0, 0, 0.3)',
                     border: '1px solid rgba(255, 255, 255, 0.1)'
                   }}>
                <p className="text-xs font-mono text-white/60">
                  {this.state.error.toString()}
                </p>
              </div>
            )}
            
            <div className="space-y-3">
              <button
                onClick={this.handleReset}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white font-medium"
                style={{ background: 'linear-gradient(135deg, #EC9C55 0%, #E2684C 50%, #80578C 100%)' }}
              >
                <RefreshCw size={16} />
                Try Again
              </button>

              <button
                onClick={this.handleGoHome}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white/80 font-medium backdrop-blur-sm"
                style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}
              >
                <Home size={16} />
                Go to Dashboard
              </button>
            </div>
            
            {this.state.errorCount > 2 && (
              <p className="text-xs text-white/50 text-center mt-4">
                If this keeps happening, try refreshing the page or clearing your browser cache.
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ScreenErrorBoundary;